import {useEffect, useState} from 'react';
import styled, {keyframes} from 'styled-components';

interface TypewriterMessageProps {
  messages: string[];
  loopStart?: number;
  slotMs?: number;
}

const TYPING_RATIO = 0.6;

const caretBlink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

const TypewriterText = styled.span`
  color: #2d2d2d;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  white-space: pre-wrap;
  word-break: break-word;
  display: inline-flex;
  align-items: center;
  flex: 1;
  min-width: 0;

  &::after {
    content: '';
    display: inline-block;
    width: 2px;
    height: 1rem;
    margin-left: 2px;
    background-color: #2d2d2d;
    animation: ${caretBlink} 1s steps(2, start) infinite;
  }
`;

export const TypewriterMessage = ({
  messages,
  loopStart = 0,
  slotMs = 4000,
}: TypewriterMessageProps) => {
  const [index, setIndex] = useState(0);
  const [charCount, setCharCount] = useState(0);

  // Slot timer: advance to next message every `slotMs`, looping back to
  // `loopStart` past the end.
  useEffect(() => {
    const slotTimer = setInterval(() => {
      setIndex((prev) => {
        const next = prev + 1;
        return next >= messages.length ? loopStart : next;
      });
    }, slotMs);
    return () => clearInterval(slotTimer);
  }, [messages.length, loopStart, slotMs]);

  // Typing timer: reveal the current message char-by-char.
  useEffect(() => {
    setCharCount(0);
    const text = messages[index] ?? '';
    if (!text) return undefined;
    const typingWindow = slotMs * TYPING_RATIO;
    const stepMs = Math.max(20, Math.floor(typingWindow / text.length));
    const typeTimer = setInterval(() => {
      setCharCount((prev) => {
        if (prev >= text.length) return prev;
        return prev + 1;
      });
    }, stepMs);
    return () => clearInterval(typeTimer);
  }, [index, messages, slotMs]);

  const current = messages[index] ?? '';
  const visible = current.slice(0, charCount);

  return <TypewriterText>{visible}</TypewriterText>;
};

export default TypewriterMessage;
